(function() {
  // Prevent multiple executions
  if (window.productCardVideoInitialized) return;
  window.productCardVideoInitialized = true;

  const initializedWrappers = new WeakSet();

  function initHoverVideos() {
    const hoverVideoWrappers = document.querySelectorAll('[data-hover-video]:not([data-video-initialized])');
    
    hoverVideoWrappers.forEach(wrapper => {
      if (initializedWrappers.has(wrapper)) return;
      initializedWrappers.add(wrapper);
      wrapper.setAttribute('data-video-initialized', '');
      
      const videoMedia = wrapper.querySelector('video-media');
      if (!videoMedia) return;
      
      // Wait for video-media to be loaded before setting up hover
      function setupHoverHandlers() {
        if (!videoMedia.hasAttribute('loaded')) {
          // Wait for loaded attribute
          const observer = new MutationObserver(() => {
            if (videoMedia.hasAttribute('loaded')) {
              observer.disconnect();
              attachHoverHandlers();
            }
          });
          observer.observe(videoMedia, { attributes: true, attributeFilter: ['loaded'] });
          
          // Also try to initialize if video element exists
          const video = videoMedia.querySelector('video');
          if (video) {
            video.addEventListener('loadedmetadata', () => {
              observer.disconnect();
              attachHoverHandlers();
            }, { once: true });
          }
        } else {
          attachHoverHandlers();
        }
      }
      
      function attachHoverHandlers() {
        wrapper.addEventListener('mouseenter', () => {
          // Use video-media's play method if available
          if (videoMedia.play) {
            videoMedia.play().catch(() => {
              // Ignore play errors (e.g., autoplay restrictions)
            });
          } else {
            // Fallback: try to play video element directly
            const video = videoMedia.querySelector('video');
            if (video) {
              video.play().catch(() => {});
            }
          }
        }, { passive: true });
        
        wrapper.addEventListener('mouseleave', () => {
          // Use video-media's pause method if available
          if (videoMedia.pause) {
            videoMedia.pause();
          } else {
            // Fallback: try to pause video element directly
            const video = videoMedia.querySelector('video');
            if (video) {
              video.pause();
            }
          }
        }, { passive: true });
      }
      
      setupHoverHandlers();
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHoverVideos);
  } else {
    initHoverVideos();
  }

  // Re-initialize on dynamic content (e.g., AJAX pagination)
  const observer = new MutationObserver(() => {
    initHoverVideos();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Also listen for Shopify section events
  document.addEventListener('shopify:section:load', initHoverVideos);
  document.addEventListener('shopify:section:reorder', initHoverVideos);

  // Handle mobile gallery video poster images
  function initMobileVideoPosters() {
    const mobileVideoContainers = document.querySelectorAll('.product-card__mobile-video-container');
    
    mobileVideoContainers.forEach(container => {
      const poster = container.querySelector('.product-card__video-poster');
      const videoMedia = container.querySelector('video-media');
      
      if (!poster || !videoMedia) return;
      
      // Function to hide poster when video is ready
      function hidePoster() {
        poster.style.opacity = '0';
        poster.style.pointerEvents = 'none';
      }
      
      // Check if video element exists
      const video = videoMedia.querySelector('video');
      if (video) {
        // Hide poster when video can play (has loaded enough data)
        if (video.readyState >= 2) {
          hidePoster();
        } else {
          video.addEventListener('loadeddata', hidePoster, { once: true });
          video.addEventListener('canplay', hidePoster, { once: true });
          video.addEventListener('playing', hidePoster, { once: true });
        }
      }
      
      // Also listen for video-media loaded event
      if (videoMedia.hasAttribute('loaded')) {
        hidePoster();
      } else {
        const observer = new MutationObserver((mutations) => {
          if (videoMedia.hasAttribute('loaded')) {
            observer.disconnect();
            hidePoster();
          }
        });
        observer.observe(videoMedia, { attributes: true, attributeFilter: ['loaded'] });
      }
    });
  }

  // Initialize mobile video posters
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileVideoPosters);
  } else {
    initMobileVideoPosters();
  }

  // Re-initialize on dynamic content
  const posterObserver = new MutationObserver(() => {
    initMobileVideoPosters();
  });

  posterObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  document.addEventListener('shopify:section:load', initMobileVideoPosters);
  document.addEventListener('shopify:section:reorder', initMobileVideoPosters);

  // Handle mobile gallery video play/pause based on visibility
  function initMobileVideoVisibility() {
    const mobileVideoContainers = document.querySelectorAll('.product-card__mobile-video-container');
    
    mobileVideoContainers.forEach(container => {
      const videoMedia = container.querySelector('video-media[autoplay]');
      if (!videoMedia) return;
      
      // Skip if already initialized
      if (videoMedia.hasAttribute('data-visibility-initialized')) return;
      videoMedia.setAttribute('data-visibility-initialized', '');
      
      const video = videoMedia.querySelector('video');
      if (!video) return;
      
      // Wait for video-media component to be fully loaded
      const waitForVideoMediaReady = (callback) => {
        if (videoMedia.hasAttribute('loaded')) {
          callback();
        } else {
          const observer = new MutationObserver((mutations) => {
            if (videoMedia.hasAttribute('loaded')) {
              observer.disconnect();
              callback();
            }
          });
          observer.observe(videoMedia, { attributes: true, attributeFilter: ['loaded'] });
          
          // Also check if video element is ready
          if (video.readyState >= 1) {
            observer.disconnect();
            callback();
          } else {
            video.addEventListener('loadedmetadata', () => {
              observer.disconnect();
              callback();
            }, { once: true });
          }
        }
      };
      
      // Find the gallery and gallery item for scroll-based visibility calculation
      const gallery = container.closest('.product-card__mobile-gallery');
      const galleryItem = container.closest('.product-card__mobile-gallery-item');
      
      // Observe the video container itself for IntersectionObserver
      const elementToObserve = container;
      
      // Track the last visibility state on the videoMedia element to avoid unnecessary calls
      if (!videoMedia._lastVisibilityState) {
        videoMedia._lastVisibilityState = null;
      }
      
      // Helper function to check if video is actually visible in the scrollable gallery
      const checkGalleryVisibility = () => {
        if (!gallery || !galleryItem) return false;
        
        const galleryRect = gallery.getBoundingClientRect();
        const itemRect = galleryItem.getBoundingClientRect();
        
        // Check horizontal visibility within the gallery
        const itemLeft = itemRect.left - galleryRect.left;
        const itemRight = itemRect.right - galleryRect.left;
        const galleryWidth = galleryRect.width;
        
        // Calculate how much of the item is visible within the gallery viewport
        const visibleLeft = Math.max(0, -itemLeft);
        const visibleRight = Math.min(itemRect.width, galleryWidth - itemLeft);
        const visibleWidth = Math.max(0, visibleRight - visibleLeft);
        const visibleRatio = itemRect.width > 0 ? visibleWidth / itemRect.width : 0;
        
        // Also check if the gallery itself is visible in the page viewport
        const pageVisibleTop = Math.max(0, galleryRect.top);
        const pageVisibleBottom = Math.min(window.innerHeight, galleryRect.bottom);
        const pageVisibleHeight = Math.max(0, pageVisibleBottom - pageVisibleTop);
        const pageVisibleRatio = galleryRect.height > 0 ? pageVisibleHeight / galleryRect.height : 0;
        
        // Video is visible only if both: 
        // 1. At least 50% of the item is visible within the gallery horizontally
        // 2. At least 50% of the gallery is visible in the page viewport vertically
        return visibleRatio >= 0.5 && pageVisibleRatio >= 0.5;
      };
      
      // Preload video to reduce lag when it starts playing
      const preloadVideo = () => {
        // Only set preload if not already set to avoid interfering
        if (!video.hasAttribute('preload') || video.getAttribute('preload') === 'none') {
          video.setAttribute('preload', 'metadata');
        }
        // Don't call video.load() as it resets the video - let it load naturally
      };
      
      // Helper function to attempt playing video with retries
      const attemptPlay = (retries = 3) => {
        if (retries <= 0) return;
        
        // Wait for video-media to be ready first
        waitForVideoMediaReady(() => {
          // Wait for video to be ready
          const tryPlay = () => {
            if (video.paused && video.readyState >= 2) {
              // Ensure video is muted for autoplay (required by browsers)
              if (!video.muted) {
                video.muted = true;
              }
              
              // Ensure playsinline for mobile
              if (!video.hasAttribute('playsinline')) {
                video.setAttribute('playsinline', '');
              }
              
              // Try video-media's play method first
              if (videoMedia.play && typeof videoMedia.play === 'function') {
                const playResult = videoMedia.play();
                if (playResult && typeof playResult.catch === 'function') {
                  playResult.catch((error) => {
                    // If play fails, try direct video play as fallback
                    if (retries > 0) {
                      setTimeout(() => {
                        const directPlay = video.play();
                        if (directPlay && typeof directPlay.catch === 'function') {
                          directPlay.catch(() => {
                            // If still failing, retry after video loads more data
                            if (retries > 1 && video.readyState < 3) {
                              video.addEventListener('canplay', () => {
                                attemptPlay(retries - 1);
                              }, { once: true });
                            }
                          });
                        }
                      }, 100);
                    }
                  });
                }
              } else {
                // Fallback to direct video element play
                const playResult = video.play();
                if (playResult && typeof playResult.catch === 'function') {
                  playResult.catch((error) => {
                    // Retry if video needs more time to load
                    if (retries > 1 && video.readyState < 3) {
                      video.addEventListener('canplay', () => {
                        attemptPlay(retries - 1);
                      }, { once: true });
                    }
                  });
                }
              }
            } else if (video.readyState < 2) {
              // Video not ready yet, wait for it
              const onReady = () => {
                if (video.paused) {
                  tryPlay();
                }
              };
              video.addEventListener('loadeddata', onReady, { once: true });
              video.addEventListener('canplay', onReady, { once: true });
              preloadVideo();
            }
          };
          
          // Try to play
          tryPlay();
        });
      };
      
      // Helper function to update video play/pause state
      const updateVideoState = (isVisible, source = 'unknown') => {
        // Only act if visibility state changed
        if (videoMedia._lastVisibilityState === isVisible) {
          return;
        }
        
        const previousState = videoMedia._lastVisibilityState;
        videoMedia._lastVisibilityState = isVisible;
        
        if (isVisible) {
          // Preload video immediately when it becomes visible
          preloadVideo();
          
          // Video is visible - play it
          if (video.paused) {
            // Use requestAnimationFrame for smoother playback start
            requestAnimationFrame(() => {
              attemptPlay();
            });
          }
        } else {
          // Video is not visible - pause it
          if (!video.paused) {
            // Pause the video directly first
            video.pause();
            // Also use video-media's pause method if available
            if (videoMedia.pause && typeof videoMedia.pause === 'function') {
              videoMedia.pause();
            }
          }
        }
      };
      
      // Create intersection observer to pause/play based on visibility
      // Use the gallery as root to check visibility within the scrollable container
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          // Skip if completely off-screen to reduce unnecessary calls
          if (!entry.isIntersecting && entry.intersectionRatio === 0) {
            return;
          }
          
          // For horizontal scrolling galleries, also check gallery-specific visibility
          const galleryVisible = checkGalleryVisibility();
          
          // Video is visible only if both IntersectionObserver and gallery check pass
          const isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5 && galleryVisible;
          
          updateVideoState(isVisible, 'IntersectionObserver');
          
        });
      }, {
        root: gallery, // Use gallery as root to check visibility within scrollable container
        rootMargin: '0px',
        threshold: [0.4, 0.5, 0.6] // Focus on thresholds around 50% to reduce calls
      });
      
      observer.observe(elementToObserve);
      
      // Also add scroll listener on the gallery container for more reliable detection
      let scrollTimeout = null;
      let rafId = null;
      
      const checkVisibilityOnScroll = () => {
        clearTimeout(scrollTimeout);
        
        // Cancel any pending animation frame
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        
        // Use requestAnimationFrame for smoother, immediate updates
        rafId = requestAnimationFrame(() => {
          // Use gallery-specific visibility check
          const isVisible = checkGalleryVisibility();
          updateVideoState(isVisible, 'ScrollHandler');
          rafId = null;
        });
        
        // Also set a timeout as fallback for very fast scrolling
        scrollTimeout = setTimeout(() => {
          if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
          const isVisible = checkGalleryVisibility();
          updateVideoState(isVisible, 'ScrollHandler');
        }, 16); // ~60fps for responsive updates
      };
      
      // Preload video when it's getting close to being visible (60% threshold for preloading)
      const checkPreloadThreshold = () => {
        if (!gallery || !galleryItem) return;
        
        const galleryRect = gallery.getBoundingClientRect();
        const itemRect = galleryItem.getBoundingClientRect();
        
        const itemLeft = itemRect.left - galleryRect.left;
        const galleryWidth = galleryRect.width;
        const visibleLeft = Math.max(0, -itemLeft);
        const visibleRight = Math.min(itemRect.width, galleryWidth - itemLeft);
        const visibleWidth = Math.max(0, visibleRight - visibleLeft);
        const visibleRatio = itemRect.width > 0 ? visibleWidth / itemRect.width : 0;
        
        // Preload when video is 60% visible (before it needs to play at 50%)
        if (visibleRatio >= 0.6 && video.readyState < 2) {
          preloadVideo();
        }
      };
      
      if (gallery) {
        gallery.addEventListener('scroll', checkVisibilityOnScroll, { passive: true });
        // Also check preload threshold on scroll
        gallery.addEventListener('scroll', checkPreloadThreshold, { passive: true });
        // Also check on initial load
        checkVisibilityOnScroll();
        checkPreloadThreshold();
      }
      
      // Store observer reference for cleanup
      videoMedia._visibilityObserver = observer;
      videoMedia._scrollHandler = checkVisibilityOnScroll;
      videoMedia._preloadHandler = checkPreloadThreshold;
      videoMedia._scrollTimeout = scrollTimeout;
      videoMedia._rafId = rafId;
      
      // Clean up observer when element is removed
      const disconnectObserver = () => {
        if (videoMedia._visibilityObserver) {
          videoMedia._visibilityObserver.disconnect();
          delete videoMedia._visibilityObserver;
        }
        if (gallery) {
          if (videoMedia._scrollHandler) {
            gallery.removeEventListener('scroll', videoMedia._scrollHandler);
            delete videoMedia._scrollHandler;
          }
          if (videoMedia._preloadHandler) {
            gallery.removeEventListener('scroll', videoMedia._preloadHandler);
            delete videoMedia._preloadHandler;
          }
        }
        if (videoMedia._scrollTimeout) {
          clearTimeout(videoMedia._scrollTimeout);
          delete videoMedia._scrollTimeout;
        }
        if (videoMedia._rafId) {
          cancelAnimationFrame(videoMedia._rafId);
          delete videoMedia._rafId;
        }
        delete videoMedia._lastVisibilityState;
        videoMedia.removeAttribute('data-visibility-initialized');
      };
      
      // Listen for when the container is removed
      const mutationObserver = new MutationObserver(() => {
        if (!document.contains(container)) {
          disconnectObserver();
          mutationObserver.disconnect();
        }
      });
      
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    });
  }

  // Initialize mobile video visibility control
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileVideoVisibility);
  } else {
    initMobileVideoVisibility();
  }

  // Re-initialize on dynamic content
  const visibilityObserver = new MutationObserver(() => {
    initMobileVideoVisibility();
  });

  visibilityObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

  document.addEventListener('shopify:section:load', initMobileVideoVisibility);
  document.addEventListener('shopify:section:reorder', initMobileVideoVisibility);
})();

